import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { mediaUrl } from '../utils/media';
import RoleBadge from '../components/RoleBadge';
import PostContent from '../components/PostContent';
import {
  MessageSquare, PenSquare, Send, Search, User, X,
  Trash2, ImagePlus, Film, ArrowLeft, Loader2, MoreVertical, Check
} from 'lucide-react';

const relTime = (iso) => {
  if (!iso) return '';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'now';
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  if (d < 604800) return `${Math.floor(d / 86400)}d`;
  return new Date(iso).toLocaleDateString();
};

// ============== INBOX PAGE ==============
const Inbox = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/inbox/conversations');
      setConversations(res.data.conversations || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep-link: /inbox?c=<id>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const c = params.get('c');
    if (c) setActiveId(c);
  }, [location.search]);

  const handleConversationClick = (id) => {
    setActiveId(id);
    navigate(`/inbox?c=${id}`, { replace: true });
  };

  const handleStarted = (convId) => {
    setComposeOpen(false);
    load();
    handleConversationClick(convId);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-6xl mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Inbox</h1>
            <p className="text-sm text-slate-500 mt-0.5">Direct messages across the StraightConnect network.</p>
          </div>
          <button
            onClick={() => setComposeOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-semibold text-sm transition-colors"
            data-testid="inbox-compose-btn"
          >
            <PenSquare className="h-4 w-4" /> New Message
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden min-h-[70vh]">
          {/* Left: Conversation list */}
          <div className={`border-r border-slate-100 ${activeId ? 'hidden md:block' : ''}`}>
            {loading ? (
              <div className="p-10 text-center text-slate-400 text-sm"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading…</div>
            ) : conversations.length === 0 ? (
              <div className="p-10 text-center">
                <MessageSquare className="h-10 w-10 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No conversations yet</p>
                <button onClick={() => setComposeOpen(true)} className="mt-3 text-sm font-semibold text-blue-600 hover:text-blue-700">Start one</button>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
                {conversations.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => handleConversationClick(c.id)}
                    className={`w-full text-left px-4 py-3 flex gap-3 items-start hover:bg-slate-50 transition-colors ${activeId === c.id ? 'bg-blue-50' : ''}`}
                    data-testid={`conv-row-${c.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                      {c.other_user?.profile_picture
                        ? <img src={mediaUrl(c.other_user.profile_picture)} alt="" className="w-full h-full object-cover" />
                        : <User className="h-5 w-5 text-slate-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-sm truncate ${c.unread > 0 ? 'font-bold text-slate-900' : 'font-semibold text-slate-800'}`}>{c.other_user?.name || 'Unknown'}</span>
                        <RoleBadge role={c.other_user?.role} size="xs" />
                      </div>
                      <p className={`text-xs mt-0.5 truncate ${c.unread > 0 ? 'text-slate-800 font-medium' : 'text-slate-500'}`}>
                        {c.last_message_sender === user?.id && 'You: '}{c.last_message || 'No messages yet'}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-[10px] text-slate-400">{relTime(c.last_message_at)}</span>
                      {c.unread > 0 && (
                        <span className="bg-blue-600 text-white text-[9px] rounded-full min-w-4 h-4 px-1 flex items-center justify-center font-bold">{c.unread > 9 ? '9+' : c.unread}</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Thread */}
          <div className={`${!activeId ? 'hidden md:flex' : 'flex'} flex-col`}>
            {activeId
              ? <Thread key={activeId} conversationId={activeId} onBack={() => { setActiveId(null); navigate('/inbox'); }} onMessageSent={load} />
              : (
                <div className="flex-1 flex items-center justify-center text-slate-400 text-sm p-10">
                  Select a conversation to start chatting
                </div>
              )}
          </div>
        </div>
      </main>

      <AnimatePresence>
        {composeOpen && <ComposeModal onClose={() => setComposeOpen(false)} onStarted={handleStarted} />}
      </AnimatePresence>
    </div>
  );
};

// ============== THREAD ==============
const Thread = ({ conversationId, onBack, onMessageSent }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState('');
  const [media, setMedia] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/inbox/conversations/${conversationId}/messages`);
      setMessages(res.data.messages || []);
      setOther(res.data.other_user);
      await api.post(`/inbox/conversations/${conversationId}/read`).catch(() => {});
    } catch { toast.error('Could not load messages'); }
    finally { setLoading(false); }
  }, [conversationId]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  useEffect(() => {
    // scroll to bottom on new messages
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async () => {
    if (!text.trim() && media.length === 0) return;
    setSending(true);
    try {
      const payload = { text: text.trim(), media: media.map((m) => ({ url: m.url, media_type: m.media_type })) };
      const res = await api.post(`/inbox/conversations/${conversationId}/messages`, payload);
      setMessages((prev) => [...prev, res.data.message]);
      setText('');
      media.forEach((m) => m.preview && URL.revokeObjectURL(m.preview));
      setMedia([]);
      onMessageSent && onMessageSent();
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (media.length + files.length > 4) { toast.error('Max 4 attachments'); return; }
    setUploading(true);
    const out = [];
    for (const f of files) {
      if (f.size > 50 * 1024 * 1024) { toast.error(`${f.name} too large`); continue; }
      try {
        const fd = new FormData();
        fd.append('file', f);
        const res = await api.post('/inbox/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        out.push({ url: res.data.url, media_type: res.data.media_type, preview: URL.createObjectURL(f) });
      } catch { toast.error(`Failed to upload ${f.name}`); }
    }
    setMedia((prev) => [...prev, ...out]);
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeMedia = (i) => {
    setMedia((prev) => {
      const c = [...prev];
      if (c[i].preview) URL.revokeObjectURL(c[i].preview);
      c.splice(i, 1);
      return c;
    });
  };

  const handleDelete = async (msgId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/inbox/messages/${msgId}`);
      setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, deleted: true, text: '', media: [] } : m));
    } catch { toast.error('Could not delete'); }
  };

  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 bg-white">
        <button onClick={onBack} className="md:hidden p-1 text-slate-500 hover:text-slate-800" data-testid="thread-back-btn"><ArrowLeft className="h-4 w-4" /></button>
        <div className="w-9 h-9 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
          {other?.profile_picture
            ? <img src={mediaUrl(other.profile_picture)} alt="" className="w-full h-full object-cover" />
            : <User className="h-4 w-4 text-slate-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-slate-900 text-sm truncate">{other?.name || '…'}</span>
            <RoleBadge role={other?.role} size="xs" />
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 bg-slate-50/60 space-y-2" data-testid="thread-messages">
        {loading ? (
          <div className="text-center text-slate-400 text-sm py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading…</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-slate-400 text-sm py-10">Say hello to <span className="font-semibold text-slate-500">{other?.name}</span> 👋</div>
        ) : messages.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'} group`} data-testid={`msg-${m.id}`}>
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 border ${mine ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-800 border-slate-200'} ${m.deleted ? 'italic opacity-70' : ''}`}>
                {m.deleted ? (
                  <span className="text-xs">Message deleted</span>
                ) : (
                  <>
                    {m.text && <PostContent text={m.text} className={`!leading-snug ${mine ? '!text-white' : '!text-slate-800'}`} />}
                    {(m.media || []).length > 0 && (
                      <div className={`grid gap-2 ${(m.media || []).length > 1 ? 'grid-cols-2' : 'grid-cols-1'} ${m.text ? 'mt-2' : ''}`}>
                        {(m.media || []).map((f, i) => (
                          f.media_type === 'video' ? (
                            <video key={i} src={mediaUrl(f.url)} controls className="rounded max-h-56 object-cover w-full bg-black" />
                          ) : (
                            <a key={i} href={mediaUrl(f.url)} target="_blank" rel="noopener noreferrer">
                              <img src={mediaUrl(f.url)} alt="" className="rounded max-h-56 object-cover w-full" />
                            </a>
                          )
                        ))}
                      </div>
                    )}
                  </>
                )}
                <div className={`flex items-center gap-1.5 mt-1 text-[10px] ${mine ? 'text-blue-100' : 'text-slate-400'}`}>
                  <span>{relTime(m.created_at)}</span>
                  {mine && !m.deleted && (
                    <button onClick={() => handleDelete(m.id)} className="opacity-0 group-hover:opacity-100 transition-opacity ml-auto pl-2 hover:text-white" title="Delete" data-testid={`msg-delete-${m.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <div className="border-t border-slate-100 bg-white p-3">
        {media.length > 0 && (
          <div className="flex gap-2 mb-2 flex-wrap">
            {media.map((m, i) => (
              <div key={i} className="relative">
                {m.media_type === 'video' ? (
                  <div className="w-14 h-14 rounded bg-slate-900 flex items-center justify-center"><Film className="h-5 w-5 text-white" /></div>
                ) : (
                  <img src={m.preview || mediaUrl(m.url)} alt="" className="w-14 h-14 rounded object-cover" />
                )}
                <button onClick={() => removeMedia(i)} className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center" data-testid={`remove-media-${i}`}><X className="h-2.5 w-2.5" /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <input ref={fileRef} type="file" accept="image/*,video/*" multiple onChange={handleFile} className="hidden" data-testid="thread-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading || media.length >= 4} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 disabled:opacity-40" title="Photo / Video" data-testid="thread-attach-btn">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
          </button>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={`Message ${other?.name || ''}…`}
            rows={1}
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none max-h-32"
            data-testid="thread-input"
          />
          <button onClick={handleSend} disabled={sending || (!text.trim() && media.length === 0)}
            className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="thread-send-btn">
            {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          </button>
        </div>
      </div>
    </>
  );
};

// ============== COMPOSE MODAL ==============
const ComposeModal = ({ onClose, onStarted }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (selected || !query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/inbox/users/search', { params: { q: query } });
        setResults(res.data.users || []);
      } catch { setResults([]); }
    }, 180);
    return () => clearTimeout(t);
  }, [query, selected]);

  const startAndSend = async () => {
    if (!selected) { toast.error('Select a recipient'); return; }
    if (!text.trim()) { toast.error('Type a message'); return; }
    setSending(true);
    try {
      const convRes = await api.post('/inbox/conversations', { to_user_id: selected.id });
      const convId = convRes.data.conversation_id;
      await api.post(`/inbox/conversations/${convId}/messages`, { text: text.trim(), media: [] });
      toast.success('Message sent');
      onStarted(convId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSending(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 10 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-bold text-slate-900">New Message</h3>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded" data-testid="compose-close-btn"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Recipient */}
          <div className="relative">
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">To</label>
            {selected ? (
              <div className="flex items-center gap-2.5 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-white overflow-hidden flex items-center justify-center border border-blue-200">
                  {selected.profile_picture ? <img src={mediaUrl(selected.profile_picture)} alt="" className="w-full h-full object-cover" /> : <User className="h-4 w-4 text-slate-500" />}
                </div>
                <div className="flex-1 flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-semibold text-slate-900">{selected.name}</span>
                  <RoleBadge role={selected.role} size="xs" />
                </div>
                <button onClick={() => { setSelected(null); setQuery(''); }} className="p-1 text-slate-400 hover:text-slate-700" data-testid="compose-clear-recipient"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Type any name…"
                    className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    data-testid="compose-recipient-input"
                  />
                </div>
                {results.length > 0 && (
                  <div className="mt-2 border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100 max-h-56 overflow-y-auto" data-testid="compose-suggestions">
                    {results.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => setSelected(u)}
                        className="w-full text-left px-3 py-2 flex items-center gap-2.5 hover:bg-slate-50 transition-colors"
                        data-testid={`compose-suggestion-${u.id}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
                          {u.profile_picture ? <img src={mediaUrl(u.profile_picture)} alt="" className="w-full h-full object-cover" /> : <User className="h-4 w-4 text-slate-500" />}
                        </div>
                        <span className="text-sm font-medium text-slate-800 truncate">{u.name}</span>
                        <RoleBadge role={u.role} size="xs" className="ml-auto" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Message */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5 block">Message</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              placeholder="Write your message…"
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
              data-testid="compose-body-input"
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Cancel</button>
          <button onClick={startAndSend} disabled={sending || !selected || !text.trim()}
            className="inline-flex items-center gap-1.5 px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="compose-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Inbox;
